import { BadgeCheck, Pencil, Plus } from 'lucide-react'
import { useMemo } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { Education, Experience, SkillSummary } from '@/hooks/use-profile'
import { proficiencyDotClasses } from '@/lib/profile-evidence-display'
import {
  profileSectionEmptyCtaClass,
  profileSectionManageButtonClass,
} from '@/lib/profile-section-styles'
import { cn } from '@/lib/utils'
import { buildProfileSkillFocusKey } from '@/utils/object-update-utils'
import { groupDemonstratedSkillsByProficiency } from '@/utils/profile-skills-display'
import { getTopSkillLinkedEvidence } from '@/utils/top-skill-evidence-links'

import { ProfileStandingSectionHeader } from './profile-standing-section-header'
import { TopSkillChip } from './top-skill-chip'

export function ProfileSkillsSection({
  skillsHighlighted,
  profileId,
  onManageSkills,
  skillsAddDisabled,
  demonstratedSkillSummaries,
  claimedOnlySkills,
  experiences,
  education,
  readOnly = false,
  title = 'Skills',
  description,
}: {
  skillsHighlighted: boolean
  profileId: number | string | undefined
  onManageSkills?: (options?: { focusAdd?: boolean }) => void
  skillsAddDisabled?: boolean
  demonstratedSkillSummaries: SkillSummary[]
  claimedOnlySkills: string[]
  experiences?: Experience[]
  education?: Education[]
  readOnly?: boolean
  title?: string
  description?: string
}) {
  const hasTopSkills = demonstratedSkillSummaries.length > 0
  const hasClaimedSkills = claimedOnlySkills.length > 0
  const hasSkills = hasTopSkills || hasClaimedSkills
  const totalCount = demonstratedSkillSummaries.length + claimedOnlySkills.length

  const proficiencyGroups = useMemo(
    () => groupDemonstratedSkillsByProficiency(demonstratedSkillSummaries),
    [demonstratedSkillSummaries]
  )

  const linkedEvidenceBySkillId = useMemo(() => {
    const map = new Map<string, ReturnType<typeof getTopSkillLinkedEvidence>>()
    for (const summary of demonstratedSkillSummaries) {
      map.set(
        summary.skill_id,
        getTopSkillLinkedEvidence(summary.skill_name, experiences, education)
      )
    }
    return map
  }, [demonstratedSkillSummaries, education, experiences])

  if (readOnly && !hasSkills) {
    return null
  }

  return (
    <Card
      className={cn(
        'group/section transition-all duration-300',
        skillsHighlighted ? 'bg-success/5 ring-2 ring-success/50' : '',
      )}
    >
      <ProfileStandingSectionHeader
        title={title}
        description={
          description ??
          (readOnly
            ? null
            : 'Your skills passport. Top skills are backed by experience, education, or certifications; add more skills to build your profile.')
        }
        action={
          !readOnly && hasSkills ? (
            <CardAction className="row-span-1 self-center">
              <Button
                type="button"
                variant="ghost"
                size="iconSm"
                className={profileSectionManageButtonClass}
                onClick={() => onManageSkills?.()}
                disabled={skillsAddDisabled}
                aria-label="Manage skills"
                title="Manage skills"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </CardAction>
          ) : undefined
        }
      />
      <CardContent className="space-y-6 pt-0">
        {!readOnly && !hasSkills ? (
          <Button
            type="button"
            variant="outline"
            className={profileSectionEmptyCtaClass}
            onClick={() => onManageSkills?.({ focusAdd: true })}
            disabled={skillsAddDisabled}
          >
            <Plus className="mr-2 h-4 w-4 shrink-0" aria-hidden />
            Add your first skill
          </Button>
        ) : null}

        {hasTopSkills ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium leading-none">Top skills</h3>
              <Tooltip delayDuration={150}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex shrink-0 rounded-sm text-success focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    aria-label="About top skills"
                  >
                    <BadgeCheck className="size-3.5" aria-hidden strokeWidth={2.5} />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  align="start"
                  sideOffset={6}
                  hideArrow
                  className="max-w-[min(100vw-2rem,280px)] rounded-lg border border-border bg-card px-3 py-2 text-left text-foreground shadow-md"
                >
                  <p className="text-xs leading-relaxed text-foreground">
                    Top skills are backed by evidence on your profile: experience, education, or
                    certifications. The more evidence, the higher the level.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="space-y-4">
              {proficiencyGroups.map(({ label, items }) => (
                <div key={label}>
                  <h4 className="mb-2 flex items-center gap-2 text-sm font-medium leading-none">
                    <span
                      className={cn('size-1.5 shrink-0 rounded-full', proficiencyDotClasses(label))}
                      aria-hidden
                    />
                    {label}
                  </h4>
                  <ul className="flex flex-wrap gap-2">
                    {items.map((summary) => (
                      <li key={summary.skill_id}>
                        <TopSkillChip
                          summary={summary}
                          linkedEvidence={linkedEvidenceBySkillId.get(summary.skill_id)}
                          displayOnly
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {hasClaimedSkills ? (
          <div className={cn('space-y-2', hasTopSkills && 'pt-3')}>
            {hasTopSkills ? (
              <h3 className="text-sm font-medium leading-none text-muted-foreground">Other Skills</h3>
            ) : null}
            <ul className="flex flex-wrap gap-2">
              {claimedOnlySkills.map((skillName) => (
                <li key={skillName}>
                  <span
                    className={cn(
                      'inline-flex items-center rounded-full border px-3 py-1 text-sm',
                      hasTopSkills
                        ? 'border-border/60 text-muted-foreground'
                        : 'border-border bg-background',
                    )}
                    {...(profileId != null
                      ? { 'data-agent-highlight': buildProfileSkillFocusKey(String(profileId), skillName) }
                      : {})}
                  >
                    {skillName}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {hasSkills ? (
          <p className="text-xs text-muted-foreground">
            {totalCount} skill{totalCount === 1 ? '' : 's'} total
          </p>
        ) : null}
      </CardContent>
    </Card>
  )
}
